import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Sorting, Export, Paging,
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';
import 'devextreme/dist/css/dx.material.blue.light.css';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from "file-saver";
import ExcelJS from "exceljs";
import API_URL from '../config';

// --- CSS for Loading Overlay ---
const styles = `
.loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.7); display: flex; justify-content: center; align-items: center; flex-direction: column; z-index: 10; }
.loading-spinner { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; }
.loading-text { margin-top: 10px; font-size: 16px; color: #333; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

// API URL 정의
const SOFT_BIN_SUMMARY_API_URL = `${API_URL}/api/oracle/soft-bin-summary-list`;
const SOFT_BIN_DETAILS_API_URL = `${API_URL}/api/oracle/soft-bin-details-list`;
//const CUST_API_URL = `${API_URL}/api/oracle/common/custs`;
//const DEVICE_API_URL = `${API_URL}/api/oracle/common/devices`;
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;


export default function SoftBinReportPage({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // --- 상태 관리 ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [selectedFactory, setSelectedFactory] = useState('PT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [lotId, setLotId] = useState('');
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [binColumns, setBinColumns] = useState([]);
  const [operList, setOperList] = useState([]);
  const [selectedOper, setSelectedOper] = useState(null);

  const formatDate = (date) => {
    if (!date) return '';
    return `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!selectedFactory) return;
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } })
      .then(res => setCustList(res.data.data || []))
      .catch(console.error);
  }, [selectedFactory]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    const params = { factory: selectedFactory, custCd: selectedCust };
    axios.get(DEVICE_API_URL, { params })
      .then(res => {
        const devices = res.data.data || [];
        setDeviceList(devices);
        setSelectedDevice(null);
      })
      .catch(console.error);
  }, [selectedFactory, selectedCust]);

  useEffect(() => {
    if (selectedFactory === 'FT01') {
      const opers = ['FTST', 'FTHT', 'FTCT','FTST2'];
      setOperList(opers);
      setSelectedOper(opers[0]);
    } else if (selectedFactory === 'PT01') {
      const opers = ['PTST', 'PTST2', 'PTST3', 'PTST4'];
      setOperList(opers);
      setSelectedOper(opers[0]);
    } else {
      setOperList([]);
      setSelectedOper(null);
    }
  }, [selectedFactory]);

  const processData = (lotSummaryData, lotBinDetails) => {
    const binStructureMap = new Map();
    lotBinDetails.forEach(bin => {
      if (bin.SBIN != null && !binStructureMap.has(bin.SBIN)) {
        binStructureMap.set(bin.SBIN, { hbin: bin.HBIN, sbin: bin.SBIN, desc: bin.BIN_DESC || `BIN ${bin.SBIN}` });
      }
    });
    const sortedBinStructures = Array.from(binStructureMap.values()).sort((a, b) => {
      if (a.hbin !== b.hbin) return a.hbin - b.hbin;
      return a.sbin - b.sbin;
    });

    const binDetailsLookup = lotBinDetails.reduce((acc, bin) => {
        const key = `${bin.LOT_ID}|${bin.MODE1}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(bin);
        return acc;
    }, {});
    
    const finalData = lotSummaryData.map((summaryRow, index) => {
      const newRow = { ...summaryRow, gridId: `${summaryRow.LOT_ID}_${summaryRow.MODE1}_${index}` };
      const key = `${summaryRow.LOT_ID}|${summaryRow.MODE1}`;
      const relevantBins = binDetailsLookup[key] || [];
      relevantBins.forEach(bin => {
        if(bin.SBIN != null) newRow[`SBIN_${bin.SBIN}`] = bin.BIN_VAL;
      });
      return newRow;
    });

    return { finalData, sortedBinStructures };
  };
  
  const fetchData = useCallback(async () => {
    if (!selectedFactory || !selectedCust || !selectedOper || !selectedDevice) {
      notify("Factory, CUST, OPER, DEVICE는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setBinColumns([]);

    try {
      const commonParams = { factory: selectedFactory, custCd: selectedCust, device: selectedDevice, lotId: lotId.trim() || 'ALL', dateFrom: formatDate(dateFrom), dateTo: formatDate(dateTo), oper: selectedOper };
      const detailsParams = { ...commonParams };
      delete detailsParams.custCd;

      const [summaryRes, detailsRes] = await Promise.all([
        axios.get(SOFT_BIN_SUMMARY_API_URL, { params: commonParams }),
        axios.get(SOFT_BIN_DETAILS_API_URL, { params: detailsParams })
      ]);

      if (summaryRes.data.retCode !== 0) throw new Error(`Summary API Error: ${summaryRes.data.retMsg}`);
      if (detailsRes.data.retCode !== 0) throw new Error(`Details API Error: ${detailsRes.data.retMsg}`);

      const { finalData, sortedBinStructures } = processData(summaryRes.data.data, detailsRes.data.data);
      
      if (finalData.length === 0) {
        notify("조회된 데이터가 없습니다.", "info", 2000);
        setData([]);
        return;
      }
      setData(finalData);

      // ★★★ 핵심 수정: HBIN 그룹핑 로직 제거 ★★★
      // `reduce`를 사용한 hbinGroups 생성을 삭제하고,
      // 정렬된 sortedBinStructures 배열을 직접 `map`으로 순회하여 개별 밴드를 생성합니다.
      const dynamicColumns = sortedBinStructures.map(binInfo => ({
        // 각 binInfo 객체마다 독립적인 최상위 HBIN 밴드를 생성합니다.
        caption: String(binInfo.hbin),
        isBand: true,
        columns: [{
          caption: String(binInfo.sbin),
          isBand: true,
          columns: [{
            caption: binInfo.desc,
            isBand: true,
            columns: [{
              dataField: `SBIN_${binInfo.sbin}`,
              caption: "",
              dataType: "number",
              alignment: "right",
              width: 80,
            }]
          }]
        }]
      }));
      setBinColumns(dynamicColumns);

    } catch (e) {
      setError(`조회 오류: ${e.message}`);
      setData([]);
      console.error("Fetch Error:", e);
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, selectedCust, selectedDevice, lotId, dateFrom, dateTo, selectedOper]);
  
  const fixedColumns = useMemo(() => 
    ([{ dataField: "FACTORY", caption: "FACTORY", width: 80 },
     { dataField: "CUSTOMER", caption: "CUSTOMER", width: 120 }, 
     { dataField: "MAT_ID", caption: "MAT_ID", width: 150 }, 
     { dataField: "OPER", caption: "OPER", width: 100 }, 
     { dataField: "PROGRAM", caption: "PROGRAM", width: 200 }, 
     { dataField: "TESTER_ID", caption: "TESTER_ID", width: 100 }, 
     { dataField: "START_TIME", caption: "START_TIME", width: 170 }, 
     { dataField: "END_TIME", caption: "END_TIME", width: 170 }, 
     { dataField: "LOT_ID", caption: "LOT_ID", width: 120 }, 
     { dataField: "MODE1", caption: "MODE1", width: 80 }, 
     { dataField: "LOT_QTY", caption: "LOT_QTY", dataType: "number", alignment: "right", width: 100 },
      { dataField: "IN_QTY", caption: "IN_QTY", dataType: "number", alignment: "right", width: 100 },
       { dataField: "OUT_QTY", caption: "OUT_QTY", dataType: "number", alignment: "right", width: 100 }, ]), []);
  
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Soft_Bin_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Soft_Bin_Report.xlsx");
        });
      });
    e.cancel = true;
  };
  
  const renderColumns = (cols) => {
    return cols.map((col, index) => {
      const key = col.dataField || col.caption || `col_${index}`;
      if (col.isBand) {
        return <Column key={key} caption={col.caption} alignment={col.alignment || 'center'}>{col.columns && renderColumns(col.columns)}</Column>;
      }
      return <Column key={key} {...col} />;
    });
  };

  return (
    <div style={{ padding: 20, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{styles}</style>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>PT Soft Bin Detail Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" width={180} />
          <SelectBox items={['PT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
          <SelectBox dataSource={operList} value={selectedOper} onValueChanged={e => setSelectedOper(e.value)} label="OPER" width={150} placeholder="Select OPER" />
          <SelectBox dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_NM" onValueChanged={e => setSelectedCust(e.value)} searchEnabled placeholder="Select CUST" label="CUST" width={220} />
          <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} placeholder="Select DEVICE" />
          <TextBox value={lotId} onValueChanged={e => setLotId(e.value)} placeholder="LOT ID (ALL)" label="LOT ID" width={180} />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold', marginTop: '10px' }}>{error}</p>}
      </div>

      <div style={{ flex: 1, position: 'relative' }}>
        {loading && <div className="loading-overlay"><div className="loading-spinner"></div><div className="loading-text">Loading...</div></div>}
        <DataGrid
          dataSource={data}
          keyExpr="gridId"
          onExporting={onExporting}
          height="100%"
          showBorders={true}
          allowColumnResizing={true}
          columnAutoWidth={true}
          hoverStateEnabled={true}
        >
          <Export enabled={true} />
          <Paging defaultPageSize={20} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} width={240} />
          <Sorting mode="multiple" />

          {/* 고정된 Summary 컬럼 렌더링 */}
          <Column caption="" fixed={true} isBand={true}>
            {fixedColumns.map(col => <Column key={col.dataField} {...col} />)}
          </Column>

          {/* 동적 Bin 컬럼 렌더링 */}
          {renderColumns(binColumns)}
        </DataGrid>
      </div>
    </div>
  );
}